import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@volca/ui/components/ui/alert";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { useApBusinessTimeZone } from "@/accounts-payable/common/use-ap-business-time-zone";
import type { ApAgentRun } from "@/accounts-payable/types";
import { staleNoticeCopy } from "@/accounts-payable/utils";
import { useBusiness } from "@/core/business";
import { StaleNoticeActions } from "./stale-notice-actions";

/**
 * Warns that a run's open proposals may not fit ServiceTitan any more: a
 * re-check found values they rest on changed, or the data they were worked out
 * from is older than the business's `ap_stale_after_hours`. A proposal made
 * days ago can be out of date (the PO was received since, say) rather than
 * wrong, and reviewers took the one for the other, so this is a warning to be
 * read before the proposals under it. With `actions`, it offers checking
 * ServiceTitan and re-running the agent, as one split button
 * (`StaleNoticeActions`). Renders nothing when no warning is due.
 *
 * `row` puts the button after the sentence, for a queue card's header, where
 * `SupersedeNotice` sits too; `column` puts it under the text, for a narrow
 * column above an action list.
 */
export const StaleDataNotice = ({
  run,
  layout = "column",
  actions = false,
  className,
}: {
  run: ApAgentRun | undefined;
  layout?: "row" | "column";
  /** Offer checking ServiceTitan and re-running the agent. */
  actions?: boolean;
  className?: string;
}) => {
  const { selectedBusiness, businessId } = useBusiness();
  const timeZone = useApBusinessTimeZone();
  const copy =
    run &&
    staleNoticeCopy(run, selectedBusiness?.ap_stale_after_hours, timeZone);
  if (!run || !copy) return null;

  const button = actions && (
    <StaleNoticeActions
      run={run}
      businessId={businessId ?? null}
      staleAfterHours={selectedBusiness?.ap_stale_after_hours}
    />
  );

  const row = layout === "row";
  // The button gets its own grid cell under the sentence. In a row it moves
  // to a third column beside the title and sentence, from `sm` up.
  return (
    <Alert
      color="warning"
      role="note"
      className={cn(
        "w-full",
        { "sm:has-[>svg]:grid-cols-[auto_1fr_auto]": row },
        className,
      )}
    >
      <WarningIcon />
      <AlertTitle>{copy.title}</AlertTitle>
      <AlertDescription>{copy.detail}</AlertDescription>
      {button && (
        <Stack
          align="start"
          className={cn("col-start-2 mt-2", {
            "sm:col-start-3 sm:row-span-2 sm:row-start-1 sm:mt-0 sm:ml-4 sm:self-center":
              row,
          })}
        >
          {button}
        </Stack>
      )}
    </Alert>
  );
};
